import type { BurstChamber, FlipPad, Hazard, PulseChart } from '../../chart.js';
import { findActiveBurstChamber } from '../../chart.js';
import {
  BURST_CHAMBER_BASE_BONUS,
  BURST_CHAMBER_BONUS_PER_TAP,
  BURST_CHAMBER_MAX_CPS,
  BURST_CHAMBER_TAP_SCORE,
  PULSE_GOOD_WINDOW_MS,
  HAZARD_INVULN_BEATS,
  HP_EMPTY_TAP,
  HP_EMPTY_TAP_GRACE_HITS,
  HP_EMPTY_TAP_NEARBY_BEATS,
  HP_GOOD,
  HP_HAZARD,
  HP_MISS_DAMAGE_BY_STREAK_INDEX,
  HP_PERFECT,
  HP_REGEN_INTERVAL_BEATS,
  HP_REGEN_PER_TICK,
  HP_REGEN_QUIET_BEATS,
  LIFELINE_HP_THRESHOLD,
  MAX_HP,
  PERFECT_WINDOW_MS,
} from '../../gameTypes.js';
import { comboMultiplier, computeAccuracy, judge } from '../../scoring.js';
import type { EngineConfig, EngineState, PadResolution } from './state.js';

export type EngineInputType = 'flipDown' | 'flipUp';

export interface EngineInput {
  beat: number;
  type: EngineInputType;
}

/**
 * `hp_changed` is emitted whenever HP moves (positive or negative). The
 * UI uses it to flash the bar; tests use it to assert exact deltas.
 *
 * `burst_tap` fires for every tap that lands inside an active burst
 * chamber; `burst_complete` fires once when the playhead crosses the end
 * of a chamber, carrying the total tap count and the bonus awarded.
 */
export interface EngineEvent {
  type:
    | 'flip'
    | 'pad_resolved'
    | 'completed'
    | 'hp_changed'
    | 'burst_tap'
    | 'burst_complete';
  beat: number;
  payload?: unknown;
}

export interface BurstTapPayload {
  chamberId: string;
  tapCount: number;
  scoreDelta: number;
}

export interface BurstCompletePayload {
  chamberId: string;
  tapCount: number;
  bonus: number;
}

export interface HpChangedPayload {
  /** Signed delta applied this tick (e.g. -6 for a miss, +1 for a perfect). */
  delta: number;
  /** HP after the delta is applied. */
  hp: number;
  /**
   * Why HP changed. Surfaces in the UI for screen-shake colouring etc.
   * `'drained'` indicates HP just hit 0 - alive will be false and
   * deathReason set to 'drained' as well. `'lifeline'` indicates a
   * fatal hit was caught and the player was floored at 1 HP.
   */
  reason:
    | 'perfect'
    | 'good'
    | 'miss'
    | 'empty_tap'
    | 'hazard'
    | 'regen'
    | 'lifeline'
    | 'drained';
  hazardId?: string;
}

/**
 * True if the player is currently inside the post-hazard invulnerability
 * window. Only hazards trigger this (not misses or empty taps), so the
 * miss/empty-tap penalties always land - those are about rhythm timing,
 * not the action-game-style "just got hit" feedback.
 */
function isHazardInvulnerable(state: EngineState, beat: number): boolean {
  if (state.lastHazardHitBeat === null) {
    return false;
  }
  return beat - state.lastHazardHitBeat < HAZARD_INVULN_BEATS;
}

/**
 * Apply an HP delta with the given reason. Caps at MAX_HP, kills the
 * player when HP reaches 0 (deathReason becomes 'drained'). Emits an
 * `hp_changed` event so callers can flash the UI / play SFX.
 *
 * Lifeline rule: a single damage event that would drop HP to 0 from a
 * "healthy" position (above LIFELINE_HP_THRESHOLD) floors at 1 instead
 * and is reported with reason='lifeline'. Stops one ambush hit from
 * ending an otherwise clean run; the next hit can still kill, so it's
 * a one-shot save not a permanent buffer.
 */
function applyHpDelta(
  state: EngineState,
  beat: number,
  delta: number,
  reason: HpChangedPayload['reason'],
  events: EngineEvent[],
  extra?: { hazardId?: string },
): void {
  if (delta === 0) {
    return;
  }
  if (delta < 0) {
    state.combo = 0;
    state.regenProgressBeats = 0;
  }
  const wasHealthy = state.hp > LIFELINE_HP_THRESHOLD;
  const oldHp = state.hp;
  const rawNewHp = state.hp + delta;
  let lifelineCaught = false;
  if (delta < 0 && wasHealthy && rawNewHp <= 0) {
    state.hp = 1;
    lifelineCaught = true;
  } else {
    state.hp = Math.max(0, Math.min(state.maxHp, rawNewHp));
  }
  const drained = state.hp <= 0;
  const effectiveReason: HpChangedPayload['reason'] = drained
    ? 'drained'
    : lifelineCaught
      ? 'lifeline'
      : reason;
  const payload: HpChangedPayload = {
    // Report the actually-applied change so floating text and HP-bar
    // animations match what the player sees on the gauge - lifeline
    // catches and clamping at maxHp would otherwise cause the toast to
    // lie about how much HP moved.
    delta: state.hp - oldHp,
    hp: state.hp,
    reason: effectiveReason,
    ...(extra?.hazardId !== undefined ? { hazardId: extra.hazardId } : {}),
  };
  events.push({ type: 'hp_changed', beat, payload });
  if (drained) {
    state.alive = false;
    state.deathReason = 'drained';
    state.deathBeat = beat;
  }
}

/**
 * Damage for the Nth consecutive miss. Reads from
 * HP_MISS_DAMAGE_BY_STREAK_INDEX, clamping to the final entry once the
 * streak runs past the table. The streak counter is incremented by the
 * caller after this returns.
 */
function missDamageForStreak(streak: number): number {
  const table = HP_MISS_DAMAGE_BY_STREAK_INDEX;
  const idx = Math.min(streak, table.length - 1);
  return table[idx];
}

/**
 * Resolve a required-pad miss: bookkeep the streak + last-miss beat,
 * apply the (decaying) HP damage. Caller is responsible for the
 * `state.misses += 1` and the pad_resolved event - this only handles
 * the HP economy so empty taps and missed expiries can share the same
 * decay table.
 */
function applyMissDamage(
  state: EngineState,
  beat: number,
  events: EngineEvent[],
): void {
  const damage = missDamageForStreak(state.consecutiveMissCount);
  state.consecutiveMissCount += 1;
  state.lastMissBeat = beat;
  applyHpDelta(state, beat, damage, 'miss', events);
}

/**
 * Whether any un-hit pad lies within HP_EMPTY_TAP_NEARBY_BEATS of the
 * given beat (in either direction). Used to waive the empty-tap HP
 * penalty when the player is wiggling the key in dead air with nothing
 * on screen to react to.
 */
function hasUnhitPadWithinEmptyTapRange(
  pads: ReadonlyArray<FlipPad>,
  hitPadIds: ReadonlySet<string>,
  beat: number,
): boolean {
  for (const pad of pads) {
    if (hitPadIds.has(pad.id)) {
      continue;
    }
    if (Math.abs(pad.beat - beat) <= HP_EMPTY_TAP_NEARBY_BEATS) {
      return true;
    }
  }
  return false;
}

export interface StepInput {
  config: EngineConfig;
  state: EngineState;
  fromBeat: number;
  toBeat: number;
  inputs: EngineInput[];
}

export interface StepOutput {
  state: EngineState;
  events: EngineEvent[];
}

const PAD_HIT_HALF_BEAT_WINDOW_FALLBACK = 0.001;

export function bpmToBeatsPerMs(bpm: number): number {
  return bpm / 60_000;
}

export function buildEngineConfig(chart: PulseChart): EngineConfig {
  const beatsPerMs = bpmToBeatsPerMs(chart.bpm);
  return {
    chart,
    perfectWindowBeats: Math.max(PERFECT_WINDOW_MS * beatsPerMs, PAD_HIT_HALF_BEAT_WINDOW_FALLBACK),
    goodWindowBeats: Math.max(PULSE_GOOD_WINDOW_MS * beatsPerMs, PAD_HIT_HALF_BEAT_WINDOW_FALLBACK),
    flipTransitionBeats: 0.18,
    burstTapMinBeatGap: chart.bpm / (60 * BURST_CHAMBER_MAX_CPS),
  };
}

function findClosestOpenPadInWindow(
  pads: FlipPad[],
  state: EngineState,
  beat: number,
  goodWindowBeats: number,
): FlipPad | null {
  let bestRequired: FlipPad | null = null;
  let bestRequiredDiff = Infinity;
  let bestOptional: FlipPad | null = null;
  let bestOptionalDiff = Infinity;
  for (const pad of pads) {
    if (state.hitPadIds.has(pad.id)) {
      continue;
    }
    const diff = Math.abs(beat - pad.beat);
    if (diff > goodWindowBeats) {
      continue;
    }
    if (pad.optional) {
      if (diff < bestOptionalDiff) {
        bestOptionalDiff = diff;
        bestOptional = pad;
      }
    } else {
      if (diff < bestRequiredDiff) {
        bestRequiredDiff = diff;
        bestRequired = pad;
      }
    }
  }
  return bestRequired ?? bestOptional;
}

function expireMissedPads(
  state: EngineState,
  config: EngineConfig,
  toBeat: number,
  events: EngineEvent[],
): void {
  for (const pad of config.chart.pads) {
    if (state.hitPadIds.has(pad.id)) {
      continue;
    }
    if (pad.optional) {
      continue;
    }
    const expiryBeat = pad.beat + config.goodWindowBeats;
    if (toBeat > expiryBeat) {
      state.hitPadIds.add(pad.id);
      const resolution: PadResolution = {
        padId: pad.id,
        verdict: 'miss',
        diffMs: PULSE_GOOD_WINDOW_MS + 1,
        beat: pad.beat,
        registeredAt: pad.beat,
      };
      state.resolutions.push(resolution);
      state.misses += 1;
      events.push({ type: 'pad_resolved', beat: pad.beat, payload: resolution });
      // A missed pad chunks HP - this is what eventually fails the run
      // when a player just stops playing or can't keep up. Damage decays
      // with the consecutive-miss streak so a single bad bar doesn't
      // snowball into death over four beats.
      applyMissDamage(state, pad.beat, events);
      if (!state.alive) {
        return;
      }
    }
  }
}

function findHazardInWindow(
  hazards: Hazard[],
  fromBeat: number,
  toBeat: number,
  surface: 'floor' | 'ceiling',
): { hazard: Hazard; collisionBeat: number } | null {
  let best: { hazard: Hazard; collisionBeat: number } | null = null;
  for (const h of hazards) {
    if (h.surface !== surface) {
      continue;
    }
    if (h.endBeat < fromBeat) {
      continue;
    }
    if (h.startBeat > toBeat) {
      continue;
    }
    const collisionBeat = Math.max(fromBeat, h.startBeat);
    if (!best || collisionBeat < best.collisionBeat) {
      best = { hazard: h, collisionBeat };
    }
  }
  return best;
}

function resolvePadHit(
  state: EngineState,
  config: EngineConfig,
  pad: FlipPad,
  inputBeat: number,
  events: EngineEvent[],
): PadResolution {
  const beatsPerMs = bpmToBeatsPerMs(config.chart.bpm);
  const diffMs = (inputBeat - pad.beat) / beatsPerMs;
  const j = judge(diffMs, 0, { goodWindowMs: PULSE_GOOD_WINDOW_MS });
  state.hitPadIds.add(pad.id);
  // judge() returns the broad `JudgeVerdict`, but Pulse only passes
  // a goodWindowMs (never okWindowMs) so 'ok' is never produced.
  // Narrow here so `PadResolution.verdict` stays exhaustive for
  // downstream rendering / scoring consumers.
  if (j.verdict === 'ok') {
    throw new Error(`Pulse engine produced unexpected 'ok' verdict (diffMs=${diffMs})`);
  }
  const resolution: PadResolution = {
    padId: pad.id,
    verdict: j.verdict,
    diffMs,
    beat: pad.beat,
    registeredAt: inputBeat,
  };
  state.resolutions.push(resolution);

  const isOptional = !!pad.optional;

  if (j.verdict === 'perfect') {
    if (!isOptional) {
      state.perfects += 1;
    }
    state.combo += 1;
    state.score += Math.round(j.baseScore * comboMultiplier(state.combo));
  } else if (j.verdict === 'good') {
    if (!isOptional) {
      state.goods += 1;
    }
    state.combo += 1;
    state.score += Math.round(j.baseScore * comboMultiplier(state.combo));
  } else {
    if (!isOptional) {
      state.misses += 1;
    }
    state.combo = 0;
  }
  if (state.combo > state.maxCombo) {
    state.maxCombo = state.combo;
  }
  events.push({ type: 'pad_resolved', beat: inputBeat, payload: resolution });

  if (!isOptional) {
    if (j.verdict === 'perfect') {
      state.consecutiveMissCount = 0;
      applyHpDelta(state, inputBeat, HP_PERFECT, 'perfect', events);
    } else if (j.verdict === 'good') {
      state.consecutiveMissCount = 0;
      applyHpDelta(state, inputBeat, HP_GOOD, 'good', events);
    } else {
      applyMissDamage(state, inputBeat, events);
    }
  }
  return resolution;
}

function checkHazardCollisionInWindow(
  state: EngineState,
  config: EngineConfig,
  fromBeat: number,
  toBeat: number,
  events: EngineEvent[],
): void {
  if (!state.alive) {
    return;
  }
  const hit = findHazardInWindow(
    config.chart.hazards,
    fromBeat,
    toBeat,
    state.currentSurface,
  );
  if (!hit) {
    return;
  }
  if (isHazardInvulnerable(state, hit.collisionBeat)) {
    return;
  }
  state.lastHazardHitBeat = hit.collisionBeat;
  applyHpDelta(state, hit.collisionBeat, HP_HAZARD, 'hazard', events, {
    hazardId: hit.hazard.id,
  });
}

function flipSurface(s: EngineState['currentSurface']): EngineState['currentSurface'] {
  return s === 'floor' ? 'ceiling' : 'floor';
}

function registerBurstTap(
  state: EngineState,
  config: EngineConfig,
  chamber: BurstChamber,
  beat: number,
  events: EngineEvent[],
): void {
  // Anti-cheat cap: drop taps arriving faster than BURST_CHAMBER_MAX_CPS.
  // No score, no tap-count bump (so the completion bonus stays capped
  // too), no event. An autoclicker at 200 cps therefore scores the same
  // as one at exactly the cap; humans rarely observe this branch in
  // normal play.
  if (
    state.lastBurstScoringBeat !== null &&
    beat - state.lastBurstScoringBeat < config.burstTapMinBeatGap
  ) {
    return;
  }
  state.lastBurstScoringBeat = beat;
  const tapCount = (state.burstTaps.get(chamber.id) ?? 0) + 1;
  state.burstTaps.set(chamber.id, tapCount);
  state.score += BURST_CHAMBER_TAP_SCORE;
  const payload: BurstTapPayload = {
    chamberId: chamber.id,
    tapCount,
    scoreDelta: BURST_CHAMBER_TAP_SCORE,
  };
  events.push({ type: 'burst_tap', beat, payload });
}

/**
 * Fire 'burst_complete' for any chamber whose end beat the playhead just
 * crossed. Adds the completion bonus (base + per-tap) to the score so a
 * burst chamber is always net-positive even if the player ignored it.
 */
function expireFinishedBursts(
  state: EngineState,
  chambers: ReadonlyArray<BurstChamber>,
  toBeat: number,
  events: EngineEvent[],
): void {
  for (const c of chambers) {
    if (state.completedBurstChambers.has(c.id)) {
      continue;
    }
    if (toBeat < c.endBeat) {
      continue;
    }
    state.completedBurstChambers.add(c.id);
    const tapCount = state.burstTaps.get(c.id) ?? 0;
    const bonus = BURST_CHAMBER_BASE_BONUS + BURST_CHAMBER_BONUS_PER_TAP * tapCount;
    state.score += bonus;
    const payload: BurstCompletePayload = {
      chamberId: c.id,
      tapCount,
      bonus,
    };
    events.push({ type: 'burst_complete', beat: c.endBeat, payload });
  }
}

export function step(input: StepInput): StepOutput {
  const { config, fromBeat, toBeat } = input;
  const state = input.state;
  const events: EngineEvent[] = [];

  if (!state.alive || state.completed) {
    return { state, events };
  }

  const sortedInputs = [...input.inputs]
    .filter((i) => i.beat >= fromBeat && i.beat <= toBeat)
    .sort((a, b) => a.beat - b.beat);

  const chambers = config.chart.burstChambers ?? [];

  for (const inp of sortedInputs) {
    advanceTo(state, config, inp.beat, events);
    if (!state.alive || state.completed) {
      break;
    }

    if (inp.type === 'flipDown') {
      // Burst chambers swallow taps before the pad/empty-tap logic runs.
      // Validation guarantees no pads sit inside a chamber, so there's
      // nothing to "miss" here - mashing is the entire point.
      const burst = findActiveBurstChamber(chambers, inp.beat);
      if (burst) {
        registerBurstTap(state, config, burst, inp.beat, events);
        continue;
      }
      const pad = findClosestOpenPadInWindow(
        config.chart.pads,
        state,
        inp.beat,
        config.goodWindowBeats,
      );
      if (pad) {
        const res = resolvePadHit(state, config, pad, inp.beat, events);
        if (res.verdict !== 'miss' && !pad.optional) {
          state.currentSurface = flipSurface(state.currentSurface);
          events.push({ type: 'flip', beat: inp.beat, payload: { padId: pad.id } });
        }
      } else {
        // No pad in the good window. We only chunk HP if there's a pad
        // somewhere on screen the player could plausibly be reacting to;
        // wiggling the key in dead air during a long quiet stretch is
        // free. Anything within HP_EMPTY_TAP_NEARBY_BEATS of the input
        // counts as "in sight" and applies the empty-tap penalty.
        //
        // Onboarding grace: until the player has banked
        // HP_EMPTY_TAP_GRACE_HITS successful required-pad hits this run,
        // empty taps are free regardless of nearby pads. New players
        // experiment with the input without understanding the rhythm
        // contract — bleeding HP from every misfire teaches them
        // "tapping is bad" instead of "tap on the beat".
        const padNearby = hasUnhitPadWithinEmptyTapRange(
          config.chart.pads,
          state.hitPadIds,
          inp.beat,
        );
        const successfulHits = state.perfects + state.goods;
        if (padNearby && successfulHits >= HP_EMPTY_TAP_GRACE_HITS) {
          applyHpDelta(state, inp.beat, HP_EMPTY_TAP, 'empty_tap', events);
          if (!state.alive) {
            break;
          }
        }
      }
    } else {
      state.flipHeld = false;
    }
  }

  if (state.alive && !state.completed) {
    advanceTo(state, config, toBeat, events);
  }

  if (state.alive && !state.completed && state.beat >= config.chart.totalBeats) {
    expireMissedPads(state, config, state.beat + config.goodWindowBeats + 1e-6, events);
    if (state.alive) {
      state.completed = true;
      events.push({ type: 'completed', beat: state.beat });
    }
  }

  return { state, events };
}

function advanceTo(
  state: EngineState,
  config: EngineConfig,
  toBeat: number,
  events: EngineEvent[],
): void {
  if (toBeat <= state.beat) {
    return;
  }
  const fromBeat = state.beat;
  state.beat = toBeat;
  expireMissedPads(state, config, toBeat, events);
  // Skip burst completion + hazard checks once the player is dead so a
  // fatal pad-miss in this same tick can't still award a chamber bonus
  // or layer hazard damage on the corpse.
  if (!state.alive) {
    return;
  }
  expireFinishedBursts(state, config.chart.burstChambers ?? [], toBeat, events);
  checkHazardCollisionInWindow(state, config, fromBeat, toBeat, events);
  if (!state.alive) {
    return;
  }
  applyPassiveRegen(state, fromBeat, toBeat, events);
}

/**
 * Bank the eligible portion of [fromBeat, toBeat] toward the next regen
 * tick and emit `+HP_REGEN_PER_TICK` events as the accumulator crosses
 * `HP_REGEN_INTERVAL_BEATS`. Eligibility means combo > 0 AND at least
 * `HP_REGEN_QUIET_BEATS` since the most recent miss; the eligible window
 * starts at max(fromBeat, lastMissBeat + QUIET_BEATS), so a miss inside
 * this same advance correctly suppresses regen until the quiet window
 * elapses. `applyHpDelta` already zeroes `regenProgressBeats` on any
 * negative delta, so a miss/hazard wipes any partial progress.
 */
function applyPassiveRegen(
  state: EngineState,
  fromBeat: number,
  toBeat: number,
  events: EngineEvent[],
): void {
  if (state.combo <= 0) {
    return;
  }
  const quietStart =
    state.lastMissBeat === null ? -Infinity : state.lastMissBeat + HP_REGEN_QUIET_BEATS;
  const eligibleStart = Math.max(fromBeat, quietStart);
  if (eligibleStart >= toBeat) {
    return;
  }
  state.regenProgressBeats += toBeat - eligibleStart;
  while (state.regenProgressBeats >= HP_REGEN_INTERVAL_BEATS) {
    state.regenProgressBeats -= HP_REGEN_INTERVAL_BEATS;
    if (state.hp >= state.maxHp) {
      // Already topped up - drain the accumulator anyway so a player who
      // sat at max HP for a while doesn't burst-heal the moment they
      // take damage.
      continue;
    }
    applyHpDelta(state, toBeat, HP_REGEN_PER_TICK, 'regen', events);
  }
}

export function summariseAccuracy(state: EngineState): number {
  return computeAccuracy(state.perfects, state.goods, 0, state.misses);
}

// Re-export so callers don't need to import from gameTypes for the cap.
export { MAX_HP };
